import { KameleoonVisitorCodeManager } from '@kameleoon/nextjs-visitor-code-manager';
import { KameleoonEventSource } from '@kameleoon/nodejs-event-source';
import {
  CredentialsType,
  CustomData,
  Environment,
  KameleoonClient,
  KameleoonError,
  SDKConfigurationType,
} from '@kameleoon/nodejs-sdk';
import { KameleoonRequester } from '@kameleoon/nodejs-requester';
import { KAMELEOON, KamErrors, KameleoonDomains } from './constants';
import { IKamFlag } from '@/store/kameleoonFlags/kameleoonFlags.interface';

// Request deduplication cache
const requestCache = new Map<string, Promise<any>>();

// Custom requester with better error handling and deduplication
class CustomKameleoonRequester extends KameleoonRequester {
  async request(url: string, options: RequestInit): Promise<Response> {
    const cacheKey = `${url}-${JSON.stringify(options)}`;
    
    // Check if request is already in progress
    if (requestCache.has(cacheKey)) {
      console.log(`Kameleoon: Deduplicating request to ${url}`);
      return requestCache.get(cacheKey);
    }

    // Create new request promise
    const requestPromise = this.executeRequest(url, options);
    requestCache.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up cache after request completes
      requestCache.delete(cacheKey);
    }
  }

  private async executeRequest(url: string, options: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Kameleoon: Request to ${url} was aborted due to timeout`);
        throw new Error('Request timeout');
      }
      
      throw error;
    }
  }
}

let client: KameleoonClient | null;
let initializationPromise: Promise<KameleoonClient | null> | null = null;

/**
 * A custom errorHandler to print error messages
 * @param {string} type - Provide an identifier to know which function is receiving an error
 * @param {unknown} error - Caught error from try/catch, this will be a JavaScript Error or the custom KameleoonError from SDK
 * @returns undefined
 */
const errorHandler = (type: string, error: unknown): void => {
  if (error instanceof KameleoonError) {
    console.log(`${error.type}: ${error.message}`);
    return;
  } else {
    console.log(`KamSdk ${type}: ${error}`);
  }
};

export const getKameleoonClient = async (): Promise<KameleoonClient | null> => {
  // If client is already initialized, return it
  if (client && client.isInitialized()) {
    return client;
  }

  // If initialization is already in progress, wait for it
  if (initializationPromise) {
    console.log('Kameleoon: Waiting for existing initialization to complete');
    return initializationPromise;
  }

  // Start new initialization and cache the promise
  console.log('Kameleoon: Starting new client initialization');
  initializationPromise = initializeKameleeonSDK();
  const result = await initializationPromise;
  
  // Clear the promise cache after completion
  initializationPromise = null;
  
  return result;
};

// initializing the global client for application (we are not connecting client data at this point)
// This should only be called server-side to avoid exposing CLIENT_SECRET
const initializeKameleeonSDK = async (): Promise<KameleoonClient | null> => {
  // Prevent initialization on client-side where CLIENT_SECRET would be exposed
  if (typeof window !== 'undefined') {
    console.warn('Kameleoon: SDK initialization skipped on client-side. Use API route instead.');
    return null;
  }

  const clientId = process.env.KAMELEOON_CLIENT_ID;
  const clientSecret = process.env.KAMELEOON_CLIENT_SECRET;

  if (!clientId || !clientSecret) {
    console.error('Kameleoon: Missing required environment variables');
    return null;
  }

  // -- Mandatory credentials (server-side only)
  const credentials: CredentialsType = {
    clientId,
    clientSecret
  };

  //Get Kam Domain
  const getDomain = (env: Environment): KameleoonDomains => {
    switch (env) {
      case Environment.Development:
        return KameleoonDomains.Development;
      case Environment.Staging:
        return KameleoonDomains.Staging;
      case Environment.Production:
        return KameleoonDomains.Production;
      default:
        return KameleoonDomains.Development;
    }
  };

  const env = process.env.NEXT_PUBLIC_KAMELEOON_ENV as Environment;

  // -- Optional configuration
  const configuration: Partial<SDKConfigurationType> = {
    updateInterval: 5,
    environment: env,
    cookieDomain: getDomain(env),
    targetingDataCleanupInterval: 5,
  };

  try {
    client = new KameleoonClient({
      siteCode: KAMELEOON.SITE_CODE,
      credentials,
      configuration,
      externals: {
        visitorCodeManager: new KameleoonVisitorCodeManager(),
        eventSource: new KameleoonEventSource(),
        requester: new CustomKameleoonRequester(),
      },
    });

    const initializeKamClient = async () => {
      await client?.initialize();
    };

    await initializeKamClient();
  } catch (error) {
    errorHandler(KamErrors.Init, error);
    client = null;
    return null;
  }
  
  return client;
};

// Reset function to clear client and initialization state (useful for testing or error recovery)
export const resetKameleoonClient = () => {
  client = null;
  initializationPromise = null;
  requestCache.clear();
  console.log('Kameleoon: Client, initialization state, and request cache reset');
};

// Clear request cache (useful for memory management)
export const clearKameleoonRequestCache = () => {
  requestCache.clear();
  console.log('Kameleoon: Request cache cleared');
};

// Client-side function to fetch Kameleoon flags when visitor code becomes available
// This calls the API route instead of directly using the SDK to avoid exposing CLIENT_SECRET
export const fetchKameleoonFlagsClientSide = async (
  visitorCode: string,
  warehouseKey: string,
  onFlagsReceived?: (flags: Record<string, IKamFlag>) => void
): Promise<Record<string, IKamFlag>> => {
  if (!visitorCode) {
    console.log('Kameleoon: No visitor code available on client side yet');
    return {};
  }

  try {
    // Call the API route instead of directly using the SDK
    // This ensures CLIENT_SECRET is never exposed to the client
    const params = new URLSearchParams({
      visitorCode,
      ...(warehouseKey && warehouseKey !== visitorCode ? { warehouseKey } : {}),
    });

    const response = await fetch(`/api/kameleoon/flags?${params.toString()}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch flags: ${response.statusText}`);
    }

    const data = await response.json();
    const flags = data.flags || {};

    if (onFlagsReceived) {
      onFlagsReceived(flags);
    }
    return flags;
  } catch (error) {
    console.error('Kameleoon: Error fetching flags on client side:', error);
    return {};
  }
};


// this is where we are using client-specific data
export const setWarehouseData = async (visitorCode: string, warehouseKey: string) => {
  try {
    const warehouseData = await client?.getVisitorWarehouseAudience({
      visitorCode: visitorCode, // spid (snowplow id)
      warehouseKey: warehouseKey, // pcid or spid if user is guest/anonymous
      customDataIndex: 5, //pcid /spid warehousekey index value of 5 corresponds to Kam Dashboard setup
    });

    if (!warehouseData) {
      client?.addData(visitorCode, new CustomData(5, ''));
    } else {
      client?.addData(visitorCode, warehouseData);
    }
  } catch (error) {
    errorHandler(KamErrors.WarehouseKey, error);
  }
};

export const getKameleoonFlags = async (
  visitorCode: string,
  warehouseKey: string
): Promise<Record<string, IKamFlag>> => {
  if (!visitorCode) {
    console.warn('Kameleoon: No visitor code provided, skipping feature flags');
    return {};
  }

  // Ensure client is initialized
  const kamClient = await getKameleoonClient();
  if (!kamClient || !kamClient.isInitialized()) {
    console.warn('Kameleoon: Client not available or not initialized');
    return {};
  }

  try {
    await setWarehouseData(visitorCode, warehouseKey);
    const activeFlags = kamClient.getVariations({ visitorCode, onlyActive: true });
    return Array.from(activeFlags.entries()).reduce((featureFlags, lineItem) => {
      const [key, value] = lineItem;
      const variationDetails = kamClient.getVariation({ visitorCode, featureKey: key });

      return {
        ...featureFlags,
        [key]: {
          ...value,
          variation: variationDetails?.key || '',
          variables: Array.from(variationDetails?.variables?.values() || []),
        },
      };
    }, {});
  } catch (error) {
    // Handle specific "body used already" errors
    if (error instanceof Error && error.message.includes('body used already')) {
      console.warn('Kameleoon: Body used already error detected, clearing request cache and retrying');
      clearKameleoonRequestCache();
      // Return empty flags to prevent further issues
      return {};
    }
    
    errorHandler(KamErrors.WarehouseKey, error);
    return {};
  }
};
